/**
 * Deepgram Live Transcription Service
 * Handles real-time audio streaming and transcription via WebSocket
 */

import { getAuth } from "firebase/auth";
import { DEEPGRAM_TOKEN_URL, DEEPGRAM_WS_URL } from "../config/urls";
import { getClientLanguage, languageHeaders } from "../utils/clientLanguage";

// nova-3-medical is English-only; Turkish is only available on the general
// nova-3 model (see https://developers.deepgram.com/docs/models-languages-overview).
//
// Language and keyterms both come from /deepgram-token, which resolves them
// from the user's specialty server-side: a Turkish specialty dictates Turkish
// even when the practitioner is browsing the app in English. The vocabulary
// list lives only in functions/modules/audio/keyterms.js.
function buildDeepgramQueryParams(language: string, keyterms: string[]): string {
  const model = language === "tr" ? "nova-3" : "nova-3-medical";
  const base = `model=${model}&punctuate=true&language=${language}&smart_format=true&mip_opt_out=true&diarize=true`;
  return base + keyterms.map((term) => `&keyterm=${encodeURIComponent(term)}`).join("");
}

export interface TranscriptCallback {
  onTranscript: (text: string, isFinal: boolean) => void;
  onError: (error: Error) => void;
  onClose: () => void;
  onOpen?: () => void;
}

export class DeepgramService {
  private socket: WebSocket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private apiKey: string | null = null;
  private fullTranscript: string = "";
  private fullTranscriptWords: Array<{ word: string; punctuated_word?: string; speaker?: number }> = [];
  private callbacks: TranscriptCallback | null = null;
  private isConnected: boolean = false;

  // Transcription language and specialty keyterms, both supplied by the
  // backend with the temp key. If Deepgram refuses a connection carrying the
  // keyterms, they are dropped for the rest of the session — a rejected
  // keyterm must never cost the user their transcript.
  private language: string = "en";
  private keyterms: string[] = [];
  private keytermsRejected: boolean = false;
  private hasOpenedOnce: boolean = false;

  // Reconnect state
  private stream: MediaStream | null = null;
  private intentionalStop: boolean = false;
  private reconnectAttempts: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly MAX_RECONNECT_ATTEMPTS = 3;
  private readonly RECONNECT_DELAYS = [1000, 2000, 4000];

  // Pause state — when paused, the Deepgram-side MediaRecorder is paused so
  // no audio reaches Deepgram (no billing, no privacy leak). A KeepAlive ping
  // is sent every 8s so Deepgram's ~12s inactivity timeout doesn't kill the WS.
  private isPaused: boolean = false;
  private keepAliveInterval: ReturnType<typeof setInterval> | null = null;
  private readonly KEEP_ALIVE_INTERVAL_MS = 8000;

  /**
   * Get a temporary API key from our backend, along with the keyterms for
   * this user's specialty.
   */
  private async getApiKey(): Promise<string> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("User not authenticated");

    const idToken = await currentUser.getIdToken();

    const response = await fetch(DEEPGRAM_TOKEN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        // The backend picks keyterms per specialty AND transcription language,
        // so it needs the same language this service puts on the socket URL.
        ...languageHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get Deepgram API key");
    }

    const data = await response.json();
    // Fall back to the UI language if the backend predates this field, so a
    // client deployed ahead of functions still transcribes in the right language.
    this.language = typeof data.language === "string" ? data.language : getClientLanguage() || "en";
    this.keyterms = Array.isArray(data.keyterms) ? data.keyterms : [];
    return data.key;
  }

  /**
   * Start live transcription
   */
  async startTranscription(
    stream: MediaStream,
    callbacks: TranscriptCallback,
  ): Promise<void> {
    // Clean up any existing connection before starting a new one
    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.onclose = null; // prevent old onclose from firing
        this.socket.close();
      }
      this.socket = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.callbacks = callbacks;
    this.stream = stream;
    this.intentionalStop = false;
    this.reconnectAttempts = 0;
    this.fullTranscript = "";
    this.fullTranscriptWords = [];
    this.isPaused = false;
    this.language = getClientLanguage() || "en";
    this.keyterms = [];
    this.keytermsRejected = false;
    this.hasOpenedOnce = false;
    this.stopKeepAlive();

    try {
      this.apiKey = await this.getApiKey();
      this.connectWebSocket();
    } catch (error) {
      console.error("[Deepgram] Error starting transcription:", error);
      callbacks.onError(error as Error);
      throw error;
    }
  }

  /**
   * Create and wire up a WebSocket connection to Deepgram.
   * Called on initial connect and on each reconnect attempt.
   */
  private connectWebSocket(): void {
    // mip_opt_out=true: opt out of Deepgram's Model Improvement Program so
    // user audio is never used to train or improve their models.
    const keyterms = this.keytermsRejected ? [] : this.keyterms;
    const wsUrl = `${DEEPGRAM_WS_URL}?${buildDeepgramQueryParams(this.language, keyterms)}`;

    this.socket = new WebSocket(wsUrl, ["token", this.apiKey!]);

    this.socket.onopen = () => {
      // If the user pressed Stop during CONNECTING, our Phase 2 already called
      // socket.close(); bail before creating a MediaRecorder we'd have to clean up.
      if (this.intentionalStop) {
        try { this.socket?.close(); } catch {}
        return;
      }
      this.isConnected = true;
      this.hasOpenedOnce = true;
      this.reconnectAttempts = 0; // reset counter on successful open
      this.startStreaming(this.stream!);
      // If user paused before or during reconnect, the new MediaRecorder
      // must respect that — otherwise audio would resume streaming without
      // user intent.
      if (this.isPaused && this.mediaRecorder?.state === "recording") {
        try { this.mediaRecorder.pause(); } catch {}
      }
      this.callbacks?.onOpen?.();
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "Results" && data.channel?.alternatives?.[0]) {
          const transcript = data.channel.alternatives[0].transcript;
          const isFinal = data.is_final;

          if (transcript) {
            if (isFinal) {
              this.fullTranscript = (this.fullTranscript.trim() + " " + transcript.trim()).trimStart();
              const words = data.channel.alternatives[0].words;
              if (words?.length) {
                if (this.fullTranscriptWords.length > 50_000) {
                  this.fullTranscriptWords.splice(0, 5_000);
                }
                this.fullTranscriptWords.push(...words);
              }
              this.callbacks?.onTranscript(this.fullTranscript, true);
            } else {
              this.callbacks?.onTranscript(this.fullTranscript + transcript, false);
            }
          }
        }
      } catch (e) {
        console.error("[Deepgram] Error parsing message:", e);
      }
    };

    // onerror fires before onclose — log only, let onclose drive reconnect logic
    this.socket.onerror = (error) => {
      console.error("[Deepgram] WebSocket error:", error);
    };

    this.socket.onclose = () => {
      this.isConnected = false;
      this.socket = null;
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;

      if (this.intentionalStop) return;

      // Never opened while sending keyterms → assume Deepgram rejected them
      // and reconnect immediately without, before spending a retry attempt.
      if (!this.hasOpenedOnce && !this.keytermsRejected && this.keyterms.length > 0) {
        console.warn("[Deepgram] Connection refused with keyterms — retrying without them");
        this.keytermsRejected = true;
        this.connectWebSocket();
        return;
      }

      if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
        const delay = this.RECONNECT_DELAYS[this.reconnectAttempts];
        console.warn(
          `[Deepgram] Connection lost, reconnecting in ${delay}ms ` +
          `(attempt ${this.reconnectAttempts + 1}/${this.MAX_RECONNECT_ATTEMPTS})`
        );
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(async () => {
          if (this.intentionalStop) return;
          try {
            this.apiKey = await this.getApiKey();
            // Re-check after the await: user may have pressed Stop while
            // getApiKey was in flight, so we must not spin up a new socket.
            if (this.intentionalStop) return;
            this.connectWebSocket();
          } catch (err) {
            console.error("[Deepgram] Reconnect failed:", err);
            this.callbacks?.onClose();
          }
        }, delay);
      } else {
        console.error(`[Deepgram] All ${this.MAX_RECONNECT_ATTEMPTS} reconnect attempts failed`);
        this.callbacks?.onClose();
      }
    };
  }

  /**
   * Start streaming audio to Deepgram
   */
  private startStreaming(stream: MediaStream): void {
    // Use MediaRecorder to capture audio chunks
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";

    this.mediaRecorder = new MediaRecorder(stream, {
      mimeType,
      audioBitsPerSecond: 16000, // Lower bitrate for faster streaming
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(event.data);
      }
    };

    // Send audio every 250ms for near real-time transcription
    this.mediaRecorder.start(250);
  }

  /**
   * Stop transcription and return full transcript.
   * Drains the MediaRecorder so the last audio chunk reaches Deepgram, then
   * sends CloseStream and awaits Deepgram's final is_final results before
   * returning. Bounded by a 3s force-close safety timeout.
   * Note: Transcript is NOT cleared here - call clearTranscript() after successful processing.
   */
  async stopTranscription(): Promise<string> {
    // Idempotent: already torn down → return current transcript
    if (this.intentionalStop && !this.socket && !this.mediaRecorder) {
      return this.fullTranscript.trim();
    }

    // Signal that this close is intentional so onclose won't trigger reconnect
    this.intentionalStop = true;
    this.isPaused = false;
    this.stopKeepAlive();

    // Cancel any pending reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Phase 1 — drain MediaRecorder. Stop is async per spec: it queues a final
    // dataavailable (last opus chunk goes over the WS) then a stop event. We
    // must wait for stop before sending CloseStream so the last chunk reaches
    // Deepgram before it considers the stream finished.
    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    if (recorder && recorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        recorder.addEventListener("stop", finish, { once: true });
        // Safety: if stop event never fires for some reason, don't hang
        setTimeout(finish, 1000);
        try {
          recorder.stop();
        } catch {
          finish();
        }
      });
    }

    // Phase 2 — send CloseStream and wait for Deepgram to flush remaining
    // is_final results into fullTranscript. Bounded by 3s force-close.
    const socket = this.socket;
    if (socket) {
      if (socket.readyState === WebSocket.OPEN) {
        await new Promise<void>((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve();
          };
          socket.addEventListener("close", finish, { once: true });
          const timeout = setTimeout(() => {
            if (socket.readyState !== WebSocket.CLOSED) {
              console.warn("[Deepgram] Force-closing WebSocket after 3s timeout");
              try { socket.close(); } catch {}
            }
            finish();
          }, 3000);
          socket.addEventListener("close", () => clearTimeout(timeout), { once: true });
          try {
            socket.send(JSON.stringify({ type: "CloseStream" }));
          } catch {
            try { socket.close(); } catch {}
            finish();
          }
        });
      } else if (socket.readyState === WebSocket.CONNECTING) {
        // Socket never fully opened — close it immediately, nothing to await
        try { socket.close(); } catch {}
      }
      // CLOSING / CLOSED — nothing to await
    }
    this.isConnected = false;

    // Return transcript but DON'T clear - allows retry on network errors
    return this.fullTranscript.trim();
  }

  /**
   * Hard, synchronous teardown for paths that don't care about the final
   * transcript (discard, component unmount). Closes the socket immediately
   * without waiting for Deepgram's flush. Suppresses reconnect.
   */
  abortTranscription(): void {
    this.intentionalStop = true;
    this.isPaused = false;
    this.stopKeepAlive();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      try { this.mediaRecorder.stop(); } catch {}
    }
    this.mediaRecorder = null;
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      try { this.socket.close(); } catch {}
    }
    this.socket = null;
    this.isConnected = false;
  }

  /**
   * Pause the Deepgram-side stream: pause the internal MediaRecorder so no
   * audio is sent to Deepgram (no billing, no privacy leak), and start a
   * KeepAlive interval so Deepgram's inactivity timeout doesn't drop the WS.
   * Idempotent.
   */
  pauseTranscription(): void {
    if (this.isPaused) return;
    this.isPaused = true;
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      try { this.mediaRecorder.pause(); } catch {}
    }
    this.startKeepAlive();
  }

  /**
   * Resume the Deepgram-side stream: stop the KeepAlive interval and
   * resume the internal MediaRecorder. Idempotent.
   */
  resumeTranscription(): void {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.stopKeepAlive();
    if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
      try { this.mediaRecorder.resume(); } catch {}
    }
  }

  private startKeepAlive(): void {
    this.stopKeepAlive(); // idempotent
    this.keepAliveInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        try {
          this.socket.send(JSON.stringify({ type: "KeepAlive" }));
        } catch {
          // socket may have transitioned to CLOSING between the readyState
          // check and the send; safe to ignore — onclose will handle teardown
        }
      }
    }, this.KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  /**
   * Clear transcript from memory - call only after successful processing
   */
  clearTranscript(): void {
    this.fullTranscript = "";
    this.fullTranscriptWords = [];
  }

  /**
   * Get diarized transcript display string (Doctor/Patient labeled)
   */
  getTranscriptDisplay(): string {
    if (this.fullTranscriptWords.length === 0) return this.fullTranscript.trim();
    const segments: Array<{ speaker: number; text: string }> = [];
    let currentSpeaker: number | null = null;
    let currentWords: string[] = [];
    for (const w of this.fullTranscriptWords) {
      const speaker = w.speaker ?? 0;
      if (speaker !== currentSpeaker) {
        if (currentWords.length > 0)
          segments.push({ speaker: currentSpeaker!, text: currentWords.join(' ') });
        currentSpeaker = speaker;
        currentWords = [w.punctuated_word || w.word];
      } else {
        currentWords.push(w.punctuated_word || w.word);
      }
    }
    if (currentWords.length > 0)
      segments.push({ speaker: currentSpeaker!, text: currentWords.join(' ') });

    const label = (n: number) => `Speaker ${n + 1}`;
    return segments.map(s => `${label(s.speaker)}: ${s.text}`).join('\n');
  }

  /**
   * Get current full transcript
   */
  getTranscript(): string {
    return this.fullTranscript.trim();
  }

  /**
   * Check if currently connected
   */
  isActive(): boolean {
    return this.isConnected;
  }
}

// Singleton instance
export const deepgramService = new DeepgramService();
